import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

type BaseDefaultFunction = Tables<"default_functions">;
type BaseDefaultFunctionInsert = TablesInsert<"default_functions">;
type BaseDefaultFunctionUpdate = TablesUpdate<"default_functions">;

export type DefaultFunctionGroup = {
  id: string;
  name: string;
  location_type: BaseDefaultFunction["location_type"] | null;
  display_order: number;
  created_at: string;
  updated_at: string;
};
export type DefaultFunctionGroupInsert = {
  name: string;
  location_type?: BaseDefaultFunction["location_type"] | null;
  display_order?: number;
};
export type DefaultFunctionGroupUpdate = Partial<DefaultFunctionGroupInsert>;
export type DefaultFunction = BaseDefaultFunction & {
  default_function_group_id?: string | null;
  default_function_group?: DefaultFunctionGroup | null;
};
export type DefaultFunctionInsert = BaseDefaultFunctionInsert & {
  default_function_group_id?: string | null;
};
export type DefaultFunctionUpdate = BaseDefaultFunctionUpdate & {
  default_function_group_id?: string | null;
};

type ZeroDefaultFunctionRow = {
  id: string;
  name: string;
  defaultFunctionGroupID?: string | null;
  displayOrder?: number | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  systemTag?: string | null;
  locationType?: DefaultFunction["location_type"] | null;
  showOnInitialize?: boolean | null;
  defaultFunctionGroup?: ZeroDefaultFunctionGroupRow | null;
};

type ZeroDefaultFunctionGroupRow = {
  id: string;
  name: string;
  locationType?: DefaultFunction["location_type"] | null;
  displayOrder?: number | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyDefaultFunction(row: ZeroDefaultFunctionRow): DefaultFunction {
  return {
    id: row.id,
    name: row.name,
    default_function_group_id: row.defaultFunctionGroupID ?? null,
    display_order: row.displayOrder ?? 0,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    system_tag: row.systemTag ?? null,
    location_type: row.locationType ?? null,
    show_on_initialize: row.showOnInitialize ?? true,
    default_function_group: row.defaultFunctionGroup
      ? toLegacyDefaultFunctionGroup(row.defaultFunctionGroup)
      : null,
  };
}

function toLegacyDefaultFunctionGroup(
  row: ZeroDefaultFunctionGroupRow,
): DefaultFunctionGroup {
  return {
    id: row.id,
    name: row.name,
    location_type: row.locationType ?? null,
    display_order: row.displayOrder ?? 0,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toZeroDefaultFunctionInput(fn: DefaultFunctionInsert, id: string) {
  return {
    id,
    name: fn.name,
    defaultFunctionGroupID: fn.default_function_group_id ?? null,
    displayOrder: fn.display_order ?? 0,
    systemTag: fn.system_tag ?? null,
    locationType: fn.location_type ?? null,
    showOnInitialize: fn.show_on_initialize ?? true,
  };
}

function toZeroDefaultFunctionUpdate(
  id: string,
  updates: DefaultFunctionUpdate,
) {
  return {
    id,
    ...(updates.default_function_group_id !== undefined
      ? { defaultFunctionGroupID: updates.default_function_group_id }
      : {}),
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.display_order !== undefined
      ? { displayOrder: updates.display_order }
      : {}),
    ...(updates.system_tag !== undefined
      ? { systemTag: updates.system_tag }
      : {}),
    ...(updates.location_type !== undefined
      ? { locationType: updates.location_type }
      : {}),
    ...(updates.show_on_initialize !== undefined
      ? { showOnInitialize: updates.show_on_initialize }
      : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useDefaultFunctions() {
  const [data, details] = useQuery(appQueries.defaultFunctions.list());
  const functions = ((data ?? []) as ZeroDefaultFunctionRow[]).map(
    toLegacyDefaultFunction,
  );

  return {
    data: functions,
    isLoading: details.type !== "complete" && functions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useDefaultFunctionGroups() {
  const [data, details] = useQuery(appQueries.defaultFunctionGroups.list());
  const groups = ((data ?? []) as ZeroDefaultFunctionGroupRow[]).map(
    toLegacyDefaultFunctionGroup,
  );

  return {
    data: groups,
    isLoading: details.type !== "complete" && groups.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateDefaultFunctionGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (group: DefaultFunctionGroupInsert) => {
    const input = {
      id: generateUUID(),
      name: group.name,
      locationType: group.location_type ?? null,
      displayOrder: group.display_order ?? 0,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.defaultFunctionGroups.create(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function group create was rejected",
      );
      return toLegacyDefaultFunctionGroup({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      group: DefaultFunctionGroupInsert,
      options?: MutationOptions<DefaultFunctionGroup>,
    ) => {
      void run(group)
        .then((data) => {
          toast.success("Function group created");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create function group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateDefaultFunctionGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: DefaultFunctionGroupUpdate;
  }) => {
    const input = {
      id,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.location_type !== undefined
        ? { locationType: updates.location_type }
        : {}),
      ...(updates.display_order !== undefined
        ? { displayOrder: updates.display_order }
        : {}),
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.defaultFunctionGroups.update(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function group update was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: DefaultFunctionGroupUpdate },
      options?: MutationOptions<void>,
    ) => {
      void run(input)
        .then(() => {
          toast.success("Function group updated");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to update function group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteDefaultFunctionGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.defaultFunctionGroups.delete({ id }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function group delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: MutationOptions<void>) => {
      void run(id)
        .then(() => {
          toast.success("Function group deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete function group: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useCreateDefaultFunction() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (fn: DefaultFunctionInsert) => {
    const input = toZeroDefaultFunctionInput(fn, generateUUID());
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.defaultFunctions.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function create was rejected",
      );
      return toLegacyDefaultFunction({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      fn: DefaultFunctionInsert,
      options?: MutationOptions<DefaultFunction>,
    ) => {
      void run(fn)
        .then((data) => {
          toast.success("Function created");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateDefaultFunction() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: DefaultFunctionUpdate;
  }) => {
    const input = toZeroDefaultFunctionUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.defaultFunctions.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function update was rejected",
      );
      return toLegacyDefaultFunction({
        ...input,
        name: input.name ?? "",
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: DefaultFunctionUpdate },
      options?: MutationOptions<DefaultFunction>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Function updated");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteDefaultFunction() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.defaultFunctions.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Function delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: MutationOptions<void>) => {
      void run(id)
        .then(() => {
          toast.success("Function deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
